import Select from 'react-select';
import React, { useCallback, useRef, useEffect } from 'react';

const SCROLL_THRESHOLD = 100; // pixels: How much scroll before closing the dropdown

// Helper function to find the closest scrollable ancestor
const getScrollParent = (node) => {
  if (!node) {
    return window; // Default to window if node is null
  }
  let parent = node.parentNode;
  while (parent && parent !== document.documentElement) {
    // Check for document node if parent becomes document itself (e.g., parent of documentElement)
    if (parent.nodeType === Node.DOCUMENT_NODE) {
        parent = window; // Treat document node as window for scroll events
        break;
    }
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    const isScrollable = overflowY.includes('auto') || overflowY.includes('scroll');

    if (isScrollable && parent.scrollHeight > parent.clientHeight) {
      return parent; // Found a scrollable parent
    }
    parent = parent.parentNode;
  }
  return window; // Fallback to window if no other scrollable parent found or reached top
};

const SelectInput = ({
  options,
  placeholder,
  name = '',
  onChange,
  value,
  onFocus,
  onBlur, // User's onBlur, will be passed through
  disabled,
  defaultInputValue,
  defaultValue,
  isOptionDisabled,
  isOptionSelected,
  isSearchable = true,
  isClearable = true,
  tracker,
  filterOption,
}) => {
  const selectRef = useRef(null);
  const scrollListenerState = useRef({
    scrollParent: null,
    initialScrollY: 0,
    handler: null, // To store the specific handler instance for removal
  });

  const _onChange = useCallback((event) => {
    if (tracker) {
      tracker(name);
    }
    if (onChange) {
      onChange(event);
    }
  }, []);

  const handleMenuOpen = useCallback(() => {
    if (!selectRef.current) return;

    let sp;
    let initialScrollYValue;

    // Try to get the controlRef, which is the DOM element for the select control
    if (selectRef.current.controlRef) {
      const controlElement = selectRef.current.controlRef;
      sp = getScrollParent(controlElement);
      initialScrollYValue = sp === window ? sp.scrollY : sp.scrollTop;
    } else {
      // Fallback if controlRef is not available (e.g., timing issues or react-select internal changes)
      console.warn("SelectInput: controlRef not found on menu open. Scroll listener will attach to window.");
      sp = window;
      initialScrollYValue = sp.scrollY;
    }
    
    scrollListenerState.current.scrollParent = sp;
    scrollListenerState.current.initialScrollY = initialScrollYValue;
    
    const specificHandler = () => {
      if (!selectRef.current || !scrollListenerState.current.scrollParent) return;

      const parent = scrollListenerState.current.scrollParent;
      const currentScrollY = parent === window ? parent.scrollY : parent.scrollTop;
      
      if (Math.abs(currentScrollY - scrollListenerState.current.initialScrollY) > SCROLL_THRESHOLD) {
        if (selectRef.current) { // Ensure ref is still valid
            selectRef.current.blur(); // Blurring the select closes the menu
        }
      }
    };
    scrollListenerState.current.handler = specificHandler;

    if (scrollListenerState.current.scrollParent) {
        scrollListenerState.current.scrollParent.addEventListener('scroll', specificHandler, true); // Use capture phase
    }
  }, []); // Empty dependency array: these functions are stable and rely on refs

  const handleMenuClose = useCallback(() => {
    if (scrollListenerState.current.scrollParent && scrollListenerState.current.handler) {
      scrollListenerState.current.scrollParent.removeEventListener('scroll', scrollListenerState.current.handler, true);
      // Reset parts of the state for clarity, though not strictly necessary if re-set on open
      scrollListenerState.current.scrollParent = null;
      scrollListenerState.current.handler = null;
      scrollListenerState.current.initialScrollY = 0;
    }
  }, []); // Empty dependency array

  // Effect for unmount cleanup
  useEffect(() => {
    return () => { // This cleanup function runs when the component unmounts
      handleMenuClose(); // Ensure listener is removed if menu was open on unmount
    };
  }, [handleMenuClose]); // Re-run if handleMenuClose changes (it won't due to useCallback)

  return (
    <Select
      ref={selectRef}
      isOptionSelected={isOptionSelected}
      isOptionDisabled={isOptionDisabled}
      isSearchable={isSearchable}
      isClearable={isClearable}
      filterOption={filterOption}
      name={name}
      onBlur={onBlur}
      onChange={_onChange}
      isDisabled={disabled}
      onFocus={onFocus}
      value={value}
      defaultInputValue={defaultInputValue}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      defaultValue={defaultValue}
      theme={(theme) => ({
        ...theme,
        // borderRadius: 0,
        colors: {
          ...theme.colors,
          primary: '#191923',
          primary25: '#F1F5F9',
        },
      })}
      menuPortalTarget={document.body} // Render menu in portal
      menuPosition="fixed" // Position menu fixed relative to viewport
      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} // Ensure high z-index for portal
      classNames={{
        control: (state) => `text-forms md:text-base flex items-center justify-between w-full border rounded-md bg-white min-h-9 h-fit sm:h-9 gap-x-0 ${disabled ? 'bg-gray-100 opacity-70' : state.isFocused ? `border-gray-500 drop-shadow-none` : `border-slate-200`}`,
        indicatorSeparator: () => '!bg-transparent',
        valueContainer: () => '!pl-3 pr-0',
        option: (state) => `cursor-pointer ${state.isFocused ? `bg-gray-100 text-black font-medium` : state.isSelected ? `bg-slate-100 text-black font-medium` : `bg-white text-black`} ${state.isDisabled && 'cursor-not-allowed opacity-70'}`,
        menu: () => `bg-white rounded-md overflow-hidden`,
        menuList: () => `py-0 no-scrollbar text-base`,
      }}
      placeholder={placeholder}
      options={options}
    />
  );
};

export default SelectInput;
